import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Loader2, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordInput } from '../components/PasswordInput';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function apiErr(err, fallback) {
  var d = err && err.response && err.response.data && err.response.data.detail;
  if (typeof d === 'string') return d;
  return fallback;
}

// Écran « Mon compte » commun aux voyageurs ET aux guides : informations
// personnelles + changement de mot de passe.
export default function Account() {
  var { t } = useTranslation();
  var navigate = useNavigate();
  var auth = useAuth();

  var [me, setMe] = React.useState(null);
  var [loading, setLoading] = React.useState(true);
  var [savingInfo, setSavingInfo] = React.useState(false);
  var [savingPwd, setSavingPwd] = React.useState(false);

  var [firstName, setFirstName] = React.useState('');
  var [lastName, setLastName] = React.useState('');

  var [currentPwd, setCurrentPwd] = React.useState('');
  var [newPwd, setNewPwd] = React.useState('');
  var [confirmPwd, setConfirmPwd] = React.useState('');

  React.useEffect(function() {
    axios.get(API_URL + '/api/auth/me', { withCredentials: true })
      .then(function(res) {
        setMe(res.data);
        setFirstName(res.data.first_name || '');
        setLastName(res.data.last_name || '');
        setLoading(false);
      })
      .catch(function() {
        navigate('/login');
      });
  }, [navigate]);

  function saveInfo(e) {
    e.preventDefault();
    setSavingInfo(true);
    axios.put(API_URL + '/api/auth/me', {
      first_name: firstName,
      last_name: lastName
    }, { withCredentials: true })
      .then(function(res) {
        setMe(res.data);
        if (auth.setUser) auth.setUser(res.data);
        toast.success(t('account.infoUpdated'));
        setSavingInfo(false);
      })
      .catch(function(err) {
        toast.error(apiErr(err, t('account.saveError')));
        setSavingInfo(false);
      });
  }

  function savePassword(e) {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error(t('account.pwMin8')); return; }
    if (newPwd !== confirmPwd) { toast.error(t('account.pwMismatch')); return; }
    setSavingPwd(true);
    axios.post(API_URL + '/api/auth/change-password', {
      current_password: currentPwd || null,
      new_password: newPwd
    }, { withCredentials: true })
      .then(function() {
        toast.success(t('account.pwUpdated'));
        setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
        setMe(Object.assign({}, me, { has_password: true }));
        setSavingPwd(false);
      })
      .catch(function(err) {
        toast.error(apiErr(err, t('account.pwError')));
        setSavingPwd(false);
      });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center pt-20">
        <Loader2 className="h-12 w-12 text-[#F9004D] animate-spin" />
      </div>
    );
  }

  var needsCurrent = me && me.has_password;

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12 px-4 sm:px-6" data-testid="account-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{t('account.title')}</h1>
          <p className="text-zinc-400 mt-1">{t('account.subtitle')}</p>
        </div>

        {/* Informations personnelles */}
        <Card className="bg-zinc-900/50 border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-[#F9004D]" />
            <h2 className="text-xl font-bold text-white">{t('account.personalInfo')}</h2>
          </div>

          <div className="mb-4">
            <Label className="text-zinc-300 mb-2 block">{t('account.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
              <Input value={me.email} disabled className="bg-black/30 border-white/10 text-zinc-400 pl-10" />
            </div>
            <p className="text-zinc-500 text-xs mt-1">{t('account.emailLocked')}</p>
          </div>

          <form onSubmit={saveInfo} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300 mb-2 block">{t('account.firstName')}</Label>
                <Input value={firstName} onChange={function(e) { setFirstName(e.target.value); }}
                  className="bg-black/20 border-white/10 text-white" data-testid="account-first-name" />
              </div>
              <div>
                <Label className="text-zinc-300 mb-2 block">{t('account.lastName')}</Label>
                <Input value={lastName} onChange={function(e) { setLastName(e.target.value); }}
                  className="bg-black/20 border-white/10 text-white" data-testid="account-last-name" />
              </div>
            </div>
            <Button type="submit" disabled={savingInfo}
              className="bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full px-6">
              {savingInfo ? <Loader2 className="h-4 w-4 animate-spin" /> : t('account.save')}
            </Button>
          </form>
        </Card>

        {/* Mot de passe */}
        <Card className="bg-zinc-900/50 border-white/10 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="h-5 w-5 text-[#00F0FF]" />
            <h2 className="text-xl font-bold text-white">
              {needsCurrent ? t('account.changePw') : t('account.setPw')}
            </h2>
          </div>

          {!needsCurrent && (
            <p className="text-zinc-400 text-sm mb-4">
              {t('account.googleHint')}
            </p>
          )}

          <form onSubmit={savePassword} className="space-y-4">
            {needsCurrent && (
              <div>
                <Label className="text-zinc-300 mb-2 block">{t('account.currentPw')}</Label>
                <PasswordInput value={currentPwd} onChange={function(e) { setCurrentPwd(e.target.value); }}
                  required placeholder="••••••••" className="bg-black/20 border-white/10 text-white"
                  data-testid="account-current-password" />
              </div>
            )}
            <div>
              <Label className="text-zinc-300 mb-2 block">{t('account.newPw')}</Label>
              <PasswordInput value={newPwd} onChange={function(e) { setNewPwd(e.target.value); }}
                required minLength={8} placeholder={t('account.atLeast8')}
                className="bg-black/20 border-white/10 text-white" data-testid="account-new-password" />
            </div>
            <div>
              <Label className="text-zinc-300 mb-2 block">{t('account.confirmNewPw')}</Label>
              <PasswordInput value={confirmPwd} onChange={function(e) { setConfirmPwd(e.target.value); }}
                required minLength={8} placeholder="••••••••"
                className="bg-black/20 border-white/10 text-white" data-testid="account-confirm-password" />
            </div>
            <Button type="submit" disabled={savingPwd}
              className="bg-[#00F0FF] hover:bg-[#00c8d4] text-black rounded-full px-6 font-bold">
              {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : (needsCurrent ? t('account.changePw') : t('account.setPwBtn'))}
            </Button>
          </form>
        </Card>

        {/* Raccourci vers l'espace correspondant au rôle */}
        <div className="text-center">
          {me.role === 'guide' ? (
            <Button variant="outline" onClick={function() { navigate('/guide/dashboard'); }}
              className="border-white/20 text-zinc-300">
              {t('account.goHost')}
            </Button>
          ) : (
            <Button variant="outline" onClick={function() { navigate('/dashboard'); }}
              className="border-white/20 text-zinc-300">
              {t('account.goBookings')}
            </Button>
          )}
        </div>

        {/* Zone dangereuse : suppression du compte (tout en bas) */}
        <Card className="bg-[#F9004D]/5 border-[#F9004D]/30 p-6 mt-6">
          <div className="flex items-center gap-2 mb-2">
            <Trash2 className="h-5 w-5 text-[#F9004D]" />
            <h2 className="text-xl font-bold text-white">{t('account.dangerZone')}</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-4">{t('account.deleteAccountDesc')}</p>
          <Button variant="outline" onClick={function() { navigate('/delete-account'); }}
            className="border-[#F9004D]/50 text-[#F9004D] hover:bg-[#F9004D]/10"
            data-testid="account-delete">
            {t('account.deleteAccountBtn')}
          </Button>
        </Card>
      </div>
    </div>
  );
}
